
import React, { useState } from 'react';
import { Filter, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Separator } from '@/components/ui/separator';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';

export type FilterOption = {
  value: string;
  label: string;
  count?: number;
};

interface FilterCategory {
  name: string;
  options: FilterOption[];
}

interface TableFiltersProps {
  categories: Record<string, { label: string; value: string; count?: number }[]>;
  activeFilters: Record<string, string[]>;
  onFilterChange: (category: string, value: string) => void;
  onClearFilters: () => void;
}

const TableFilters: React.FC<TableFiltersProps> = ({
  categories,
  activeFilters,
  onFilterChange,
  onClearFilters
}) => {
  const [open, setOpen] = useState(false);
  
  // Count total active filters
  const activeFilterCount = Object.values(activeFilters)
    .reduce((count, values) => count + values.length, 0);

  return (
    <div className="flex flex-wrap items-center gap-2">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button 
            variant="outline" 
            size="sm"
            className={cn(
              "h-8 border-dashed gap-1",
              activeFilterCount > 0 ? "border-blue-500 text-blue-500" : "border-muted-foreground"
            )}
          >
            <Filter className="h-3.5 w-3.5" />
            <span>Filters</span>
            {activeFilterCount > 0 && (
              <Badge 
                variant="secondary" 
                className="ml-1 rounded-full px-1 py-0 text-xs"
              >
                {activeFilterCount}
              </Badge>
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent 
          className="w-[420px] max-w-[90vw] p-0" 
          align="start" 
          side="bottom"
          sideOffset={5}
          alignOffset={0}
          avoidCollisions={true}
        >
          <div className="flex items-center justify-between px-4 py-2 border-b">
            <h4 className="font-medium">Filters</h4>
            {activeFilterCount > 0 && (
              <Button 
                variant="ghost" 
                size="sm" 
                className="h-8 px-2 text-muted-foreground" 
                onClick={() => {
                  onClearFilters();
                  setOpen(false);
                }}
              >
                Clear all
                <X className="ml-1 h-3 w-3" />
              </Button>
            )}
          </div>
          <div className="py-2 max-h-[70vh] overflow-y-auto">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {Object.entries(categories).map(([categoryName, options]) => (
                <div key={categoryName} className="px-4 py-2">
                  <h5 className="mb-1 text-sm font-medium">{categoryName}</h5>
                  <div className="space-y-1">
                    {options.map((option) => {
                      const isActive = activeFilters[categoryName]?.includes(option.value);
                      
                      return (
                        <div
                          key={option.value}
                          className={cn(
                            "flex items-center justify-between px-2 py-1 text-sm rounded-md cursor-pointer hover:bg-muted",
                            isActive && "bg-muted"
                          )}
                          onClick={() => onFilterChange(categoryName, option.value)}
                        >
                          <span>{option.label}</span>
                          {option.count !== undefined && (
                            <Badge variant="outline" className="ml-auto">
                              {option.count}
                            </Badge>
                          )}
                        </div>
                      );
                    })}
                  </div>
                  <Separator className="my-2" />
                </div>
              ))}
            </div>
          </div>
        </PopoverContent>
      </Popover>
      
      {/* Display active filter badges */}
      {Object.entries(activeFilters).map(([category, values]) =>
        values.map((value) => {
          // Find the label for this value
          const option = categories[category]?.find(o => o.value === value);
          
          return (
            <Badge
              key={`${category}-${value}`}
              variant="outline"
              className="h-8 gap-1 border-blue-500 text-blue-500 px-3"
            >
              <span>{category}: {option?.label || value}</span>
              <Button
                variant="ghost"
                size="sm"
                className="h-4 w-4 p-0 ml-1"
                onClick={() => onFilterChange(category, value)}
              >
                <X className="h-3 w-3" />
              </Button>
            </Badge>
          );
        })
      )}
    </div>
  );
};

export default TableFilters;
